import { MonthlyUsageStats } from '@/api/apiService';
import { Body1 } from '@fluentui/react-components';
import React from 'react';

const SERIES = [
    { key: 'unique_users', label: 'Users', color: 'var(--colorBrandForeground1)' },
    { key: 'unique_sessions', label: 'Sessions', color: 'var(--colorPaletteGreenForeground1)' },
    { key: 'unique_messages', label: 'Queries', color: 'var(--colorPaletteDarkOrangeForeground1)' },
] as const;

const WIDTH = 728;
const HEIGHT = 260;
const PAD = { top: 12, right: 16, bottom: 28, left: 40 };
const TICKS = 4;

// Round the axis top up to a whole step of 1, 2 or 5 x 10^n so tick labels stay integers.
const niceMax = (value: number) => {
    const raw = Math.max(value, 1) / TICKS;
    const magnitude = Math.max(1, Math.pow(10, Math.floor(Math.log10(raw))));
    const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? raw;
    return step * TICKS;
};

const shortMonth = (month: string) =>
    new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
    });

const UsageTrendChart: React.FC<{ stats: MonthlyUsageStats[] }> = ({ stats }) => {
    const months = [...stats].reverse();
    const max = niceMax(Math.max(...months.flatMap((m) => SERIES.map((s) => m[s.key]))));
    const plotWidth = WIDTH - PAD.left - PAD.right;
    const plotHeight = HEIGHT - PAD.top - PAD.bottom;
    const x = (i: number) =>
        PAD.left + (months.length === 1 ? plotWidth / 2 : (i * plotWidth) / (months.length - 1));
    const y = (v: number) => PAD.top + plotHeight * (1 - v / max);

    return (
        <div>
            <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="img"
                aria-label="Monthly usage trend"
                style={{ width: '100%', height: 'auto', marginTop: '12px' }}
            >
                {Array.from({ length: TICKS + 1 }, (_, t) => {
                    const value = (max / TICKS) * t;
                    return (
                        <g key={t}>
                            <line
                                x1={PAD.left}
                                x2={WIDTH - PAD.right}
                                y1={y(value)}
                                y2={y(value)}
                                stroke="var(--colorNeutralStroke2)"
                            />
                            <text
                                x={PAD.left - 8}
                                y={y(value)}
                                textAnchor="end"
                                dominantBaseline="middle"
                                fontSize={11}
                                fill="var(--colorNeutralForeground3)"
                            >
                                {value}
                            </text>
                        </g>
                    );
                })}
                {months.map((m, i) => (
                    <text
                        key={m.date}
                        x={x(i)}
                        y={HEIGHT - 8}
                        textAnchor="middle"
                        fontSize={11}
                        fill="var(--colorNeutralForeground3)"
                    >
                        {shortMonth(m.date)}
                    </text>
                ))}
                {SERIES.map((s) => (
                    <g key={s.key} data-series={s.key}>
                        <polyline
                            fill="none"
                            stroke={s.color}
                            strokeWidth={2}
                            points={months.map((m, i) => `${x(i)},${y(m[s.key])}`).join(' ')}
                        />
                        {months.map((m, i) => (
                            <circle key={m.date} cx={x(i)} cy={y(m[s.key])} r={3} fill={s.color}>
                                <title>{`${s.label}, ${shortMonth(m.date)}: ${m[s.key]}`}</title>
                            </circle>
                        ))}
                    </g>
                ))}
            </svg>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '4px' }}>
                {SERIES.map((s) => (
                    <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span
                            aria-hidden
                            style={{ width: '12px', height: '3px', backgroundColor: s.color, display: 'inline-block' }}
                        />
                        <Body1>{s.label}</Body1>
                    </span>
                ))}
            </div>
        </div>
    );
};

export default UsageTrendChart;
